import { createSearchParams } from 'react-router-dom'

import { IconType } from '@factorialco/f0-react'
import {
  ArrowCycle,
  Briefcase,
  Calendar,
  FileSigned,
  List,
  MessageFilled,
  Money,
  Pencil,
  People,
  Person,
  Settings,
} from '@factorialco/f0-react/icons/app'

import i18n from 'modules/core/lib/i18n'
import type { Navigation, NavigationPaths } from 'modules/core/lib/navigation/context'

import {
  CONTRACT_CHANGE_TYPES,
  EMPLOYEE_CHANGE_TYPES,
  LEAVE_CHANGE_TYPES,
  MANUAL_CHANGE_TYPES,
} from 'modules/payroll_bundle/lib/Incidences/Types'

interface SidebarItem {
  key: string
  name: string
  path: string
  count?: number
}

export type ItemsType = {
  key: string
  label: string
  icon: IconType
  count?: number
  path: () => string
  subitems?: SidebarItem[]
  useNewIcon?: boolean
}

export const incidenceTypeNavigation = {
  ALL: 'all',
  ALL_WITH_MESSAGES: 'all-with-messages',
  NEW_HIRES: 'new-hires',
  CONTRACT_UPDATES: 'contract-updates',
  PERSONAL_DATA: 'personal-data',
  ABSENCES: 'absences',
  MANUAL_INCIDENCES: 'manual-incidences',
  TERMINATIONS: 'terminations',
}

const leaveTypeNavigation = {
  SICK_LEAVE: 'sick-leave',
  PARENTAL_LEAVE: 'parental-leave',
  CUSTOM_LEAVE: 'custom-leave',
}

const personalTypeNavigation = {
  PERSONAL_ID: 'personal-id',
  PERSONAL_NAME: 'personal-name',
  PERSONAL_IRPF: 'personal-irpf',
  PERSONAL_BANK: 'personal-bank',
  PERSONAL_GENDER: 'personal-gender',
  PERSONAL_ADDRESS: 'personal-address',
  PERSONAL_RESIDENCY: 'personal-residency',
  PERSONAL_WORKPLACE: 'personal-workplace',
  PERSONAL_NATIONALITY: 'personal-nationality',
  PERSONAL_LEGAL_ENTITY: 'personal-legal-entity',
}

const subtypesNavigation = {
  ...leaveTypeNavigation,
  ...personalTypeNavigation,
}

const getLeaveTypeNavigationMapping = () => ({
  [leaveTypeNavigation.SICK_LEAVE]: [LEAVE_CHANGE_TYPES.sick],
  [leaveTypeNavigation.PARENTAL_LEAVE]: [LEAVE_CHANGE_TYPES.parental],
  [leaveTypeNavigation.CUSTOM_LEAVE]: [LEAVE_CHANGE_TYPES.custom_leave],
})

const getEmployeeTypeNavigationMapping = () => ({
  [personalTypeNavigation.PERSONAL_ID]: [EMPLOYEE_CHANGE_TYPES.id],
  [personalTypeNavigation.PERSONAL_NAME]: [EMPLOYEE_CHANGE_TYPES.name],
  [personalTypeNavigation.PERSONAL_IRPF]: [EMPLOYEE_CHANGE_TYPES.irpf],
  [personalTypeNavigation.PERSONAL_BANK]: [EMPLOYEE_CHANGE_TYPES.bank],
  [personalTypeNavigation.PERSONAL_GENDER]: [EMPLOYEE_CHANGE_TYPES.gender],
  [personalTypeNavigation.PERSONAL_ADDRESS]: [EMPLOYEE_CHANGE_TYPES.address],
  [personalTypeNavigation.PERSONAL_RESIDENCY]: [EMPLOYEE_CHANGE_TYPES.residence],
  [personalTypeNavigation.PERSONAL_WORKPLACE]: [EMPLOYEE_CHANGE_TYPES.workplace],
  [personalTypeNavigation.PERSONAL_NATIONALITY]: [EMPLOYEE_CHANGE_TYPES.nationality],
  [personalTypeNavigation.PERSONAL_LEGAL_ENTITY]: [EMPLOYEE_CHANGE_TYPES.legal_entity],
})

export const getIncidenceTypeNavigationMapping = () => {
  const { hiring, termination, ...employeeChangeTypes } = EMPLOYEE_CHANGE_TYPES

  return {
    [incidenceTypeNavigation.ALL]: undefined,
    [incidenceTypeNavigation.ALL_WITH_MESSAGES]: undefined,
    [incidenceTypeNavigation.NEW_HIRES]: [hiring],
    [incidenceTypeNavigation.CONTRACT_UPDATES]: Object.values(CONTRACT_CHANGE_TYPES),
    [incidenceTypeNavigation.PERSONAL_DATA]: Object.values(employeeChangeTypes),
    ...getEmployeeTypeNavigationMapping(),
    [incidenceTypeNavigation.ABSENCES]: Object.values(LEAVE_CHANGE_TYPES),
    ...getLeaveTypeNavigationMapping(),
    [incidenceTypeNavigation.MANUAL_INCIDENCES]: [MANUAL_CHANGE_TYPES.manual_incidence],
    [incidenceTypeNavigation.TERMINATIONS]: [termination],
  }
}

const sidebarNavItems = {
  ...incidenceTypeNavigation,
  PAYROLL_RESULTS: 'payroll-results',
  CYCLES: 'Cycles',
  PREFERENCES: 'preferences',
}

const createIncidenceNavItemPath = (
  navigation: Navigation<NavigationPaths>,
  incidenceType: string
) => {
  const incidencesRoute = navigation.bookkeepers.incidences.fullPath()
  const searchParams = createSearchParams({ incidenceType })
  return `${incidencesRoute}?${searchParams}`
}

const incidenceNavItems = (navigation: Navigation<NavigationPaths>): ItemsType[] => {
  return [
    {
      key: sidebarNavItems.ALL,
      label: i18n.t('bookkeepers.sidebar.all_incidences'),
      icon: List,
      path: () => navigation.bookkeepers.incidences.fullPath(),
    },
    {
      key: sidebarNavItems.NEW_HIRES,
      label: i18n.t('bookkeepers.sidebar.new_hires'),
      icon: People,
      path: () => createIncidenceNavItemPath(navigation, sidebarNavItems.NEW_HIRES),
    },
    {
      key: sidebarNavItems.CONTRACT_UPDATES,
      label: i18n.t('bookkeepers.sidebar.contract_updates'),
      icon: FileSigned,
      path: () => createIncidenceNavItemPath(navigation, sidebarNavItems.CONTRACT_UPDATES),
    },
    {
      key: sidebarNavItems.PERSONAL_DATA,
      label: i18n.t('bookkeepers.sidebar.personal_data'),
      icon: Person,
      path: () => createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_ID),
      subitems: [
        {
          key: subtypesNavigation.PERSONAL_ID,
          name: i18n.t('bookkeepers.sidebar.personal_id'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_ID),
        },
        {
          key: subtypesNavigation.PERSONAL_NAME,
          name: i18n.t('bookkeepers.sidebar.personal_name'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_NAME),
        },
        {
          key: subtypesNavigation.PERSONAL_IRPF,
          name: i18n.t('bookkeepers.sidebar.personal_irpf'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_IRPF),
        },
        {
          key: subtypesNavigation.PERSONAL_BANK,
          name: i18n.t('bookkeepers.sidebar.personal_bank'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_BANK),
        },
        {
          key: subtypesNavigation.PERSONAL_GENDER,
          name: i18n.t('bookkeepers.sidebar.personal_gender'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_GENDER),
        },
        {
          key: subtypesNavigation.PERSONAL_ADDRESS,
          name: i18n.t('bookkeepers.sidebar.personal_address'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_ADDRESS),
        },
        {
          key: subtypesNavigation.PERSONAL_RESIDENCY,
          name: i18n.t('bookkeepers.sidebar.personal_residency'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_RESIDENCY),
        },
        {
          key: subtypesNavigation.PERSONAL_WORKPLACE,
          name: i18n.t('bookkeepers.sidebar.personal_workplace'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_WORKPLACE),
        },
        {
          key: subtypesNavigation.PERSONAL_NATIONALITY,
          name: i18n.t('bookkeepers.sidebar.personal_nationality'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_NATIONALITY),
        },
        {
          key: subtypesNavigation.PERSONAL_LEGAL_ENTITY,
          name: i18n.t('bookkeepers.sidebar.personal_legal_entity'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PERSONAL_LEGAL_ENTITY),
        },
      ],
    },
    {
      key: sidebarNavItems.ABSENCES,
      label: i18n.t('bookkeepers.sidebar.absences'),
      icon: Calendar,
      path: () => createIncidenceNavItemPath(navigation, subtypesNavigation.SICK_LEAVE),
      subitems: [
        {
          key: subtypesNavigation.SICK_LEAVE,
          name: i18n.t('bookkeepers.sidebar.sick_leave'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.SICK_LEAVE),
        },
        {
          key: subtypesNavigation.PARENTAL_LEAVE,
          name: i18n.t('bookkeepers.sidebar.parental_leave'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.PARENTAL_LEAVE),
        },
        {
          key: subtypesNavigation.CUSTOM_LEAVE,
          name: i18n.t('bookkeepers.sidebar.custom_leave'),
          path: createIncidenceNavItemPath(navigation, subtypesNavigation.CUSTOM_LEAVE),
        },
      ],
    },
    {
      key: sidebarNavItems.MANUAL_INCIDENCES,
      label: i18n.t('bookkeepers.sidebar.manual_incidences'),
      icon: Pencil,
      path: () => createIncidenceNavItemPath(navigation, sidebarNavItems.MANUAL_INCIDENCES),
    },
    {
      key: sidebarNavItems.TERMINATIONS,
      label: i18n.t('bookkeepers.sidebar.terminations'),
      icon: Briefcase,
      path: () => createIncidenceNavItemPath(navigation, sidebarNavItems.TERMINATIONS),
    },
    {
      key: sidebarNavItems.ALL_WITH_MESSAGES,
      label: i18n.t('bookkeepers.sidebar.all_with_messages'),
      icon: MessageFilled,
      path: () => createIncidenceNavItemPath(navigation, sidebarNavItems.ALL_WITH_MESSAGES),
    },
  ]
}

export const payrollNavItems = (
  navigation: Navigation<NavigationPaths>,
  payrollResultsCount: number
): ItemsType[] => {
  return [
    {
      key: sidebarNavItems.PAYROLL_RESULTS,
      label: i18n.t('bookkeepers.sidebar.payroll_results'),
      icon: Money,
      path: () => navigation.bookkeepers.clients.payrollResults.fullPath(),
      count: payrollResultsCount,
    },
  ]
}

const cyclesMenuItem = (
  navigation: Navigation<NavigationPaths>,
  cyclesCounts: number,
  showCycleNavigation: boolean
): ItemsType[] => {
  if (showCycleNavigation) {
    return [
      {
        key: sidebarNavItems.CYCLES,
        label: i18n.t('bookkeepers.sidebar.cycles'),
        icon: ArrowCycle,
        path: () => navigation.bookkeepers.clients.payrollCycles.fullPath(),
        count: cyclesCounts,
        useNewIcon: true,
      },
    ]
  } else return []
}

const payrollResultsMenuItem = (
  navigation: Navigation<NavigationPaths>,
  payrollResultsCount: number,
  showPayrollNavigation: boolean
): ItemsType[] => {
  if (showPayrollNavigation) {
    return [
      {
        key: sidebarNavItems.PAYROLL_RESULTS,
        label: i18n.t('bookkeepers.sidebar.payroll_results'),
        icon: Money,
        path: () => navigation.bookkeepers.clients.payrollResults.fullPath(),
        count: payrollResultsCount,
      },
    ]
  } else return []
}

export const cycleNavItems = (
  navigation: Navigation<NavigationPaths>,
  payrollResultsCount: number,
  cyclesCounts: number,
  showCycleNavigation: boolean,
  showPayrollNavigation: boolean
): ItemsType[] => {
  return payrollResultsMenuItem(navigation, payrollResultsCount, showPayrollNavigation).concat(
    cyclesMenuItem(navigation, cyclesCounts, showCycleNavigation)
  )
}

export const preferenceNavItems = (navigation: Navigation<NavigationPaths>): ItemsType[] => {
  return [
    {
      key: sidebarNavItems.PREFERENCES,
      label: i18n.t('bookkeepers.sidebar.configuration'),
      icon: Settings,
      path: () => navigation.bookkeepers.clients.preferences.fullPath(),
    },
  ]
}

export const accountMenuOptions = (navigation: Navigation<NavigationPaths>) => {
  return [
    {
      href: navigation.dashboard.fullPath(),
      title: i18n.t('bookkeepers.header.your_factorial_account'),
    },
    {
      href: navigation.bookkeepers.settings.fullPath(),
      title: i18n.t('bookkeepers.header.your_settings'),
    },
    {
      href: navigation.logout.fullPath(),
      title: i18n.t('bookkeepers.header.logout'),
    },
  ]
}

export const translatedTitle = (incidenceType: string) => {
  const translations = {
    [incidenceTypeNavigation.ALL_WITH_MESSAGES]: i18n.t('bookkeepers.sidebar.all_with_messages'),
    [incidenceTypeNavigation.NEW_HIRES]: i18n.t('bookkeepers.sidebar.new_hires'),
    [incidenceTypeNavigation.CONTRACT_UPDATES]: i18n.t('bookkeepers.sidebar.contract_updates'),
    [incidenceTypeNavigation.PERSONAL_DATA]: i18n.t('bookkeepers.sidebar.personal_data'),
    [incidenceTypeNavigation.ABSENCES]: i18n.t('bookkeepers.sidebar.absences'),
    [incidenceTypeNavigation.MANUAL_INCIDENCES]: i18n.t('bookkeepers.sidebar.manual_incidences'),
    [incidenceTypeNavigation.TERMINATIONS]: i18n.t('bookkeepers.sidebar.terminations'),
    [leaveTypeNavigation.SICK_LEAVE]: i18n.t('bookkeepers.sidebar.sick_leave'),
    [leaveTypeNavigation.PARENTAL_LEAVE]: i18n.t('bookkeepers.sidebar.parental_leave'),
    [leaveTypeNavigation.CUSTOM_LEAVE]: i18n.t('bookkeepers.sidebar.custom_leave'),
    [personalTypeNavigation.PERSONAL_ADDRESS]: i18n.t('bookkeepers.sidebar.personal_address'),
    [personalTypeNavigation.PERSONAL_BANK]: i18n.t('bookkeepers.sidebar.personal_bank'),
    [personalTypeNavigation.PERSONAL_GENDER]: i18n.t('bookkeepers.sidebar.personal_gender'),
    [personalTypeNavigation.PERSONAL_ID]: i18n.t('bookkeepers.sidebar.personal_id'),
    [personalTypeNavigation.PERSONAL_IRPF]: i18n.t('bookkeepers.sidebar.personal_irpf'),
    [personalTypeNavigation.PERSONAL_NAME]: i18n.t('bookkeepers.sidebar.personal_name'),
    [personalTypeNavigation.PERSONAL_NATIONALITY]: i18n.t(
      'bookkeepers.sidebar.personal_nationality'
    ),
    [personalTypeNavigation.PERSONAL_RESIDENCY]: i18n.t('bookkeepers.sidebar.personal_residency'),
    [personalTypeNavigation.PERSONAL_WORKPLACE]: i18n.t('bookkeepers.sidebar.personal_workplace'),
  }

  return translations[incidenceType] ?? i18n.t('bookkeepers.sidebar.all_incidences')
}

const incidenceCountByTypeToNavItemsMapping = {
  [incidenceTypeNavigation.ALL]: 'all_incidences',
  [incidenceTypeNavigation.NEW_HIRES]: 'all_hiring',
  [incidenceTypeNavigation.CONTRACT_UPDATES]: 'all_contracts',
  [incidenceTypeNavigation.PERSONAL_DATA]: 'all_personal_data',
  [incidenceTypeNavigation.ABSENCES]: 'all_leaves',
  [incidenceTypeNavigation.MANUAL_INCIDENCES]: 'all_manual_incidences',
  [incidenceTypeNavigation.TERMINATIONS]: 'all_terminations',
}

const incidenceCountByTypeToSubNavItemsMapping = {
  [personalTypeNavigation.PERSONAL_ID]: 'id',
  [personalTypeNavigation.PERSONAL_NAME]: 'name',
  [personalTypeNavigation.PERSONAL_IRPF]: 'irpf',
  [personalTypeNavigation.PERSONAL_BANK]: 'bank',
  [personalTypeNavigation.PERSONAL_GENDER]: 'gender',
  [personalTypeNavigation.PERSONAL_ADDRESS]: 'address',
  [personalTypeNavigation.PERSONAL_RESIDENCY]: 'residence',
  [personalTypeNavigation.PERSONAL_WORKPLACE]: 'workplace',
  [personalTypeNavigation.PERSONAL_NATIONALITY]: 'nationality',
  [personalTypeNavigation.PERSONAL_LEGAL_ENTITY]: 'legal_entity',
  [leaveTypeNavigation.SICK_LEAVE]: 'sick',
  [leaveTypeNavigation.PARENTAL_LEAVE]: 'parental',
  [leaveTypeNavigation.CUSTOM_LEAVE]: 'custom_leave',
}

const incidenceSubNavItemsWithCount = (
  subitems: SidebarItem[],
  incidenceCountsByType: Record<string, number>
) => {
  return subitems.map((subitem) => {
    const incidenceCountKey = incidenceCountByTypeToSubNavItemsMapping[subitem.key]
    subitem.count = incidenceCountsByType[incidenceCountKey ?? '']

    return subitem
  })
}

export const incidenceNavItemsWithCount = (
  navigation: Navigation<NavigationPaths>,
  incidenceCountsByType: Record<string, number>,
  incidencesUnreadMessagesCount: number
) => {
  return incidenceNavItems(navigation).map((navItem) => {
    const incidenceCountKey = incidenceCountByTypeToNavItemsMapping[navItem.key]

    if (incidenceCountKey === 'all_incidences') {
      navItem.count = allIncidenceCount(incidenceCountsByType)
    } else if (navItem.key === sidebarNavItems.ALL_WITH_MESSAGES) {
      navItem.count = incidencesUnreadMessagesCount
    } else navItem.count = incidenceCountsByType[incidenceCountKey ?? '']

    if (navItem.subitems) {
      navItem.subitems = incidenceSubNavItemsWithCount(navItem.subitems, incidenceCountsByType)
    }

    return navItem
  })
}

const allIncidenceCount = (incidenceCountsByType: Record<string, number>) => {
  return Object.keys(incidenceCountsByType).reduce((total, key) => {
    if (Object.values(incidenceCountByTypeToNavItemsMapping).includes(key)) {
      total += incidenceCountsByType[key] ?? 0
    }

    return total
  }, 0)
}

export const matchSubPathByIncidenceType = (incidenceType: string, itemKey: string) => {
  const subItemsMapping: Record<string, Array<string>> = {
    ...getLeaveTypeNavigationMapping(),
    ...getEmployeeTypeNavigationMapping(),
  }
  const activeSubItem = subItemsMapping[incidenceType]?.[0] || null

  if (activeSubItem) {
    const mapping = getIncidenceTypeNavigationMapping()

    return !!mapping[itemKey]?.includes(activeSubItem)
  }

  return false
}

const matchSubPathByFullPath = (fullPath: string, item: ItemsType) =>
  item.subitems?.find((subitem) => fullPath.includes(subitem.path))

const matchPathByIncidenceType = (incidenceType: string, itemKey: string) => {
  return itemKey === incidenceType || matchSubPathByIncidenceType(incidenceType, itemKey)
}

const matchPathByFullPath = (fullPath: string, item: ItemsType) => {
  /**
   * We use `includes` over `===` because some item path when derived will
   * include url params like `:id` (eg. /professionals/clients/payroll-results/:id)
   * this will not match if we use `===`
   */
  return fullPath.includes(item.path()) || !!matchSubPathByFullPath(fullPath, item)
}

export const matchActiveNavItem = (fullPath: string, item: ItemsType, incidenceType?: string) => {
  if (incidenceType) {
    return matchPathByIncidenceType(incidenceType, item.key)
  } else {
    return matchPathByFullPath(fullPath, item)
  }
}

export const showIncidenceTypeColumn = [
  leaveTypeNavigation.CUSTOM_LEAVE,
  incidenceTypeNavigation.ALL_WITH_MESSAGES,
]
